import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Box, SxProps, useTheme } from "@mui/material";
import { Loading } from "shared-ui/global/Loading";
import React, { ChangeEvent, ReactNode, createRef, useState } from "react";
import { useSnackbar } from "../../Snackbar/SnackbarContext";

const dropZoneStyle = {
  backgroundColor: "info.light",
  borderColor: "info.main",
};

const inputHoverStyle = {
  "&:hover": {
    cursor: "pointer",
    ...dropZoneStyle,
  },
};

interface ImageFileDragAndDropProps {
  onUpload: (url: string) => void;
  children?: ReactNode;
  sx?: SxProps;
  loading?: boolean;
  hasError?: boolean;
  errorMessage?: ReactNode;
  disabled?: boolean;
  overrideContainerStyle?: boolean;
}

const ImageFileDragAndDrop = ({
  onUpload,
  children,
  sx,
  loading,
  hasError,
  errorMessage,
  disabled,
  overrideContainerStyle,
}: ImageFileDragAndDropProps) => {
  const { setMessage } = useSnackbar();
  const {
    palette: { greyscale },
  } = useTheme();

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (disabled) {
      return;
    }
    setInDropZone(true);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (disabled) {
      return;
    }
    event.dataTransfer.dropEffect = "copy";
    setInDropZone(true);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setInDropZone(false);
    if (disabled) {
      return;
    }
    const files = event.dataTransfer.files;
    await convertFileToUrl(validateFile(files));
  };

  const handleDragExit = (event: React.DragEvent<HTMLDivElement>) => {
    setInDropZone(false);
  };

  const [inDropZone, setInDropZone] = useState(false);
  const inputRef = createRef<HTMLInputElement>();
  const openFileFinder = () => {
    if (inputRef && inputRef.current) {
      inputRef.current.click();
    }
  };

  const convertFileToUrl = async (file: File | null): Promise<void> => {
    if (file) {
      const url = await getUrlPromiseFromFile(file);
      onUpload(url);
    }
  };

  const getUrlPromiseFromFile = (file: File) => {
    return new Promise<string>(resolve => {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result) {
          resolve(reader.result.toString());
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const validateFile = (files: FileList | null): File | null => {
    if (files) {
      if (files.length !== 1) {
        setMessage("error", `Please import one image at a time.`);
        return null;
      }
      const validatedFile = Array.from(files).filter(file => {
        if (file.type.startsWith("image")) {
          return true;
        } else {
          setMessage("error", `'${file.name}' is not a image file and will not be uploaded.`);
          return false;
        }
      });
      return validatedFile[0];
    }
    return null;
  };

  const imageUploadViewContainerStyle: SxProps = !overrideContainerStyle
    ? {
        ...sx,
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        border: `1px solid ${greyscale[200]}`,
        borderRadius: 0.5,
        flexDirection: "column",
        gap: 1,
        backgroundColor: "greyscale.50",
        ...(!disabled && inputHoverStyle),
        ...(inDropZone && !disabled && dropZoneStyle),
      }
    : { ...sx };

  return (
    <Box
      role={disabled ? undefined : "button"}
      tabIndex={disabled ? undefined : 0}
      aria-label={disabled ? undefined : "Upload image"}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragExit={handleDragExit}
      onDragLeave={handleDragExit}
      onClick={disabled ? undefined : openFileFinder}
      onKeyDown={
        disabled
          ? undefined
          : event => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openFileFinder();
              }
            }
      }
      sx={{
        ...imageUploadViewContainerStyle,
        ...(!disabled && {
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
          },
        }),
      }}
    >
      {loading ? (
        <Loading />
      ) : hasError ? (
        <>
          <InfoOutlined color="error" />
          {errorMessage}
        </>
      ) : (
        <>
          {!overrideContainerStyle && !loading && (
            <Box
              sx={{
                display: "flex",
                backgroundColor: disabled ? "greyscale.100" : "greyscale.300",
                height: 40,
                width: 40,
                borderRadius: 8,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CloudUploadIcon color={disabled ? "disabled" : undefined} fontSize="small" />
            </Box>
          )}
          {children}
        </>
      )}
      <input
        disabled={disabled}
        type="file"
        style={{ display: "none" }}
        ref={inputRef}
        accept="image/*"
        onChange={async (event: ChangeEvent<HTMLInputElement>) => await convertFileToUrl(validateFile(event.target.files))}
      />
    </Box>
  );
};

export default ImageFileDragAndDrop;
